// client/src/components/pages/AmendmentsAllocateTransactions/amendmentFields.ts
//
// The rules the Amendments editors share (ST-00221): the inline cell, Find & Replace, the
// inline-edit save and the Bulk Amend modal.
import type { AmendmentRejectionBody } from '../../../types/clientPages/amendments';

/**
 * The numeric weight columns, listed explicitly (as server/utils/amendmentUtils WEIGHT_FIELDS).
 * Matching on the word "weight" also caught Time Between Weights ('0:04:22') and the weigh
 * timestamps (AMD-19).
 */
export const WEIGHT_FIELDS: ReadonlySet<string> = new Set([
  'First Weight',
  'Second Weight',
  'Nett Weight',
  'Delivery Weight',
  'Variance Weight',
]);

export const isWeightField = (field: string): boolean => WEIGHT_FIELDS.has(field);

/** A plain decimal number with a point: "51.35", "-2", ".5", "0". */
const NUMBER_PATTERN = /^[-+]?(\d+(\.\d*)?|\.\d+)$/;

/**
 * Whether a weight value can be saved. A blank weight was saved as NULL, so the data view fell
 * back to the raw weighbridge weight (AMD-21); the server now refuses '' and non-numbers with
 * 400 { invalidFields }. A comma decimal ("0,00") is refused too: in a number input it reads as ''.
 */
export const isValidWeight = (value: unknown): boolean =>
  typeof value === 'number'
    ? Number.isFinite(value)
    : typeof value === 'string' && NUMBER_PATTERN.test(value.trim());

/** The message shown when a weight is refused. */
export const weightMessage = (field: string): string =>
  `${field} must be a number (use a point as decimal separator, e.g. 51.35).`;

/**
 * The message of a refused amendment (400 { message, invalidFields | unknownFields |
 * invalidLocations }), naming the refused fields / locations when the message does not already.
 */
export const rejectionMessage = (
  data: AmendmentRejectionBody | null | undefined
): string | null => {
  if (!data?.message) return null;
  const message = data.message;
  const named = [
    ...(data.invalidFields || []),
    ...(data.unknownFields || []),
    ...(data.invalidLocations || []),
  ];
  const missing = named.filter((name) => !message.includes(name));
  return missing.length > 0 ? `${message} (${missing.join(', ')})` : message;
};
